


























































































import Vue from 'vue';
import { mapMutations, mapState, Store } from 'vuex';
import { validationMixin } from 'vuelidate';
import { email, maxLength, required } from 'vuelidate/lib/validators';
import { Source } from '@/constants/source';
import { PaymentSystem } from '@/constants/paymentSystem';
import { onlyJAorEN } from '@/constants/validation';
import LoaderData from '@/components/common/LoaderData.vue';
import LoaderResponse from '@/components/common/LoaderResponse.vue';
import BookingDetails from '@/components/common/public/BookingDetails.vue';
import HotelMainInfo from '@/components/common/public/HotelMainInfo.vue';
import BookingPriceDetails from '@/components/common/public/BookingPriceDetails.vue';
import TermsOfPromotionsPopup from '@/components/common/public/TermsOfPromotionsPopup.vue';
import TheToolTip from '@/components/common/TheToolTip.vue';
import { CustomerDataInterface } from '@/types/store/landing/booking/customerDataInterface';
import { BookingDataInterface } from '@/types/store/landing/booking/bookingDataInterface';
import { BookingDetailsInterface } from '@/types/store/landing/booking/bookingDetailsInterface';
import { RatePlanInterface, RoomDataInterface } from '@/types/store/landing/hotels/roomDataInterface';
import { HotelDataInterface } from '@/types/store/landing/hotels/hotelDataInterface';
import { PrefectureInterface } from '@/types/common/prefectureInterface';

export default Vue.extend({
  name: 'PersonalDetails',
  components: {
    LoaderData,
    LoaderResponse,
    HotelMainInfo,
    BookingDetails,
    BookingPriceDetails,
    TermsOfPromotionsPopup,
    TheToolTip,
  },
  mixins: [validationMixin],
  data: () => ({
    form: {} as CustomerDataInterface,
    dataLoading: false,
    submitLoading: false,
    useTravelDiscount: false,
  }),
  computed: {
    ...mapState({
      paymentSystem: (state: any): PaymentSystem | undefined => state.landing.settings.payment_system,
      accountSource: (state: any): Source => state.landing.settings.account_source,
      booking: (state: any): BookingDataInterface => state.landing.booking.data,
      bookingDetails: (state: any): BookingDetailsInterface => state.landing.booking.bookingDetails,
      customer: (state: any): CustomerDataInterface => state.landing.booking.customer,
      hotel: (state: any): HotelDataInterface => state.landing.hotel.item,
      roomType: (state: any): RoomDataInterface => state.landing.hotel.room,
      prefectures: (state: any): PrefectureInterface => state.landing.settings.prefectures,
    }),
    prefecturesList(): PrefectureInterface {
      return { ...this.prefectures, ...{ 0: 'non-japan' } };
    },
    isJTB(): boolean {
      return this.paymentSystem === PaymentSystem.JTB;
    },
    isTlLinkoln(): boolean {
      return this.accountSource === Source.TlLincoln;
    },
    isTemairazu(): boolean {
      return this.accountSource === Source.Temairazu;
    },
    namePlaceholder(): string {
      return this.isTlLinkoln && this.$i18n.locale === 'ja' ? 'Yamada Taro' : '';
    },
    isDiscountCampaign(): boolean {
      return this.isCampaignHotel && this.hasDiscount;
    },
    isCampaignHotel(): boolean {
      if (this.hotel && this.hotel.in_campaign) {
        return this.hotel.in_campaign;
      }
      return false;
    },
    disableCheckbox(): boolean {
      if (!this.isCampaignHotel || !this.hasDiscount) {
        return true;
      }
      if (!this.form.prefecture_code) {
        return true;
      } else {
        return +this.form.prefecture_code === 0;
      }
    },
    ratePlan(): RatePlanInterface | undefined {
      const plan: RatePlanInterface[] = this.filterRatePlan(
        this.roomType.rate_plans,
        this.booking.meal_option,
        this.bookingDetails.payment_method,
      );
      if (plan.length) {
        return plan[0];
      }
      return undefined;
    },
    hasDiscount(): boolean {
      return !!this.ratePlan && Math.abs(this.ratePlan.discount_amount) > 0;
    },
    bookingPrice(): number {
      if (this.ratePlan) {
        return this.useTravelDiscount ? this.ratePlan.amount_with_discount : this.ratePlan.amount;
      }
      return 0;
    },
    withoutDiscount(): number {
      return this.ratePlan ? this.ratePlan.amount : 0;
    },
    sevenDaysDiscount(): boolean {
      const nightsCount: number = this.nightsCount(this.booking.checkin, this.booking.checkout);
      return !!this.ratePlan && !!this.ratePlan.discount_details && nightsCount > 7;
    },
    amountAffectedByDiscount(): number {
      return !!this.ratePlan && !!this.ratePlan.discount_details ? this.ratePlan.discount_details.effective_amount : 0;
    },
    discount(): number {
      return this.ratePlan ? this.ratePlan.discount_amount : 0;
    },
    couponAmount(): number {
      return !!this.ratePlan && !!this.ratePlan.discount_details ? this.ratePlan.discount_details.coupons_value : 0;
    },
    withDiscount(): number {
      return this.ratePlan ? this.ratePlan.amount_with_discount : 0;
    },
  },
  watch: {
    form(newV, oldV) {
      if (!oldV || newV.prefecture_code !== oldV.prefecture_code) {
        this.checkDiscountStatus();
      }
    },
    disableCheckbox(newV) {
      if (newV) {
        this.useTravelDiscount = false;
      }
    },
    'form.prefecture_code'() {
      this.useTravelDiscount = this.form.prefecture_code > 0;
    },
  },
  created() {
    if (!window.location.hash) {
      this.$router.push('/');
    }
    const form: CustomerDataInterface = JSON.parse(JSON.stringify(this.customer));
    this.form = { ...form };
  },
  validations() {
    const rules: any = {
      name: { required, onlyJAorEN },
      email: { required, email },
      phone: {
        required,
        validPhone(value: string) {
          if (value === '') {
            return true;
          }
          const phoneReg: RegExp = /^[+]*[0-9-]+$/;
          return phoneReg.test(value);
        },
      },
      address: { required, onlyJAorEN },
    };
    if (this.isTemairazu) {
      rules.phone.validTemaPhone = maxLength(15);
    }
    return {
      form: rules,
    };
  },
  methods: {
    ...mapMutations({
      setCustomer: 'landing/booking/setCustomer',
      setCustomerBackup: 'landing/booking/setCustomerBackup',
      setDiscountStatus: 'landing/booking/setDiscountStatus',
    }),
    checkDiscountStatus(): void {
      if (this.hotel.in_campaign) {
        this.useTravelDiscount = !this.disableCheckbox;
      }
    },
    setRouteBack(): void {
      if (window && typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'back_to_options', {
          items: [this.form],
        });
      }
      this.$router.push({ path: '/', hash: window.location.hash });
    },
    onSubmit(): void {
      if (!this.validateBeforeSubmit('form')) {
        return;
      }
      this.submitLoading = true;
      const data: CustomerDataInterface = JSON.parse(JSON.stringify(this.form));
      this.setCustomer(data);
      this.setCustomerBackup(data);
      this.setDiscountStatus(this.hasDiscount && this.useTravelDiscount);
      if (window && typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'begin_checkout', {
          items: [data],
        });
      }
      this.submitLoading = false;
      this.$router.push({ path: '/payment', hash: window.location.hash });
    },
  },
});
